import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import api, { formatCrypto } from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, Search, Download, AlertTriangle, Landmark, Save } from "lucide-react";

const TONE = {
  completed:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed:     "bg-rose-50 text-rose-700 border-rose-200",
  cancelled:  "bg-slate-100 text-slate-700 border-slate-200",
  processing: "bg-amber-50 text-amber-700 border-amber-200",
  initiated:  "bg-sky-50 text-sky-700 border-sky-200",
  refunded:   "bg-sky-50 text-sky-700 border-sky-200",
};
const STATUSES = ["", "initiated", "processing", "completed", "failed", "cancelled", "refunded"];
const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const shorten = (a) => (a && a.length > 16 ? `${a.slice(0, 8)}…${a.slice(-6)}` : a || "—");

/** Admin — consumer Buy Crypto orders (search, filters, CSV export). */
export default function AdminBuyOrders() {
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (status) p.set("status", status);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/buy/admin/orders?${p.toString()}`);
      setOrders(r.data.orders || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error("Couldn't load buy orders.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const exportCsv = async () => {
    try {
      const r = await api.get(`/buy/admin/orders.csv${status ? `?status=${status}` : ""}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "buy_orders.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  return (
    <AdminLayout title="Buy Crypto orders">
      <TreasuryWalletsPanel />
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="admin-buy-toolbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search id, email, session, asset…"
            className="pl-9 rounded-xl" data-testid="admin-buy-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-10 rounded-xl border border-input bg-background px-3 text-sm"
          data-testid="admin-buy-status-filter">
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s ? `${s} (${counts[s] || 0})` : "All statuses"}</option>
          ))}
        </select>
        <Button variant="outline" size="sm" className="rounded-xl gap-1.5" onClick={load}
          data-testid="admin-buy-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</Button>
        <Button variant="outline" size="sm" className="rounded-xl gap-1.5" onClick={exportCsv}
          data-testid="admin-buy-export"><Download className="w-3.5 h-3.5" /> CSV</Button>
      </div>

      <div className="xch-card overflow-x-auto" data-testid="admin-buy-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Paid</th>
              <th className="px-4 py-3">Asset</th>
              <th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">Custody wallet</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="admin-buy-empty">No orders.</td></tr>
            )}
            {!loading && orders.map((o) => (
              <tr key={o.id} className="border-b border-border/60 hover:bg-secondary/40" data-testid={`admin-buy-row-${o.id}`}>
                <td className="px-4 py-3 font-mono text-xs">
                  {o.id}
                  {o.needs_ops_review && (
                    <span className="inline-flex items-center gap-1 text-amber-600 ml-2" title={o.ops_review_reason}>
                      <AlertTriangle className="w-3.5 h-3.5" />
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">{o.user_email || "—"}</td>
                <td className="px-4 py-3 font-medium">{o.fiat_amount} {o.fiat}</td>
                <td className="px-4 py-3">≈ {formatCrypto(o.est_crypto)} {o.symbol}<div className="text-[11px] text-muted-foreground">{o.network}</div></td>
                <td className="px-4 py-3">{o.method_label || o.method}</td>
                <td className="px-4 py-3 font-mono text-xs">{shorten(o.destination_address)}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={`capitalize ${TONE[o.status] || ""}`}>{o.status}</Badge>
                  {o.failure_reason && <div className="text-[11px] text-rose-600 mt-0.5">{o.failure_reason}</div>}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{fmt(o.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

/** Treasury wallet configuration — per asset & blockchain (admin/treasury roles). */
function TreasuryWalletsPanel() {
  const [cfg, setCfg] = useState(null);
  const [wallets, setWallets] = useState({});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/buy/admin/config").then((r) => {
      setCfg(r.data);
      setWallets(r.data.treasury_wallets || {});
    }).catch(() => toast.error("Couldn't load treasury configuration."));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/buy/admin/config", { treasury_wallets: wallets });
      toast.success("Treasury wallets updated.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed — check address formats.");
    } finally { setSaving(false); }
  };

  return (
    <div className="xch-card p-5 mb-6" data-testid="admin-treasury-panel">
      <button className="w-full flex items-center justify-between" onClick={() => setOpen((v) => !v)}
        data-testid="admin-treasury-toggle">
        <div className="flex items-center gap-2 font-display font-semibold text-sm">
          <Landmark className="w-4 h-4 text-primary" /> Treasury wallets
          <Badge variant="outline" className="ml-1">{cfg ? `custody: ${cfg.custody_mode}` : "…"}</Badge>
        </div>
        <span className="text-xs text-muted-foreground">{open ? "Hide" : "Show / edit"}</span>
      </button>
      {open && cfg && (
        <div className="mt-4">
          <div className="text-xs text-muted-foreground mb-3">
            Purchases settle to these company wallets, auto-selected by asset &amp; blockchain.
            Addresses are validated per network on save. Changes apply instantly — no redeploy.
          </div>
          <div className="space-y-2">
            {(cfg.assets || []).map((a) => (
              <div key={a.key} className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2 items-center"
                data-testid={`admin-treasury-row-${a.key}`}>
                <div className="text-sm">
                  <span className="font-semibold">{a.symbol}</span>
                  <span className="text-muted-foreground text-xs ml-1.5">{a.network}</span>
                </div>
                <Input value={wallets[a.key] || ""} spellCheck={false}
                  onChange={(e) => setWallets((w) => ({ ...w, [a.key]: e.target.value.trim() }))}
                  className="font-mono text-xs rounded-xl"
                  data-testid={`admin-treasury-input-${a.key}`} />
              </div>
            ))}
          </div>
          <Button size="sm" className="rounded-xl gap-1.5 mt-4" onClick={save} disabled={saving}
            data-testid="admin-treasury-save">
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save wallets"}
          </Button>
        </div>
      )}
    </div>
  );
}
